import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Title, useNotify, useTranslate } from 'react-admin';
import { Box, CircularProgress } from '@mui/material';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import AccountFormWizard from './AccountFormWizard';
import { getAccountById } from '../services/accountService';
import { accountDetailToFormValues } from '../transforms/accountDetailToFormValues';
import { createEmptyAccountFormValues } from '../constants';
import type { AccountFormValues } from '../types/account';

export const AccountEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const translate = useTranslate();
  const notify = useNotify();
  const [values, setValues] = useState<AccountFormValues | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await getAccountById(id);
        if (cancelled) return;
        setValues(accountDetailToFormValues(detail));
      } catch (error: unknown) {
        notify(
          extractErrorMessage(
            error,
            'resources.accounts.notifications.loadFailed',
            translate
          ),
          { type: 'error' }
        );
        setValues(createEmptyAccountFormValues());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, notify, translate]);

  if (loading || !values) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Title
        title={translate('resources.accounts.account.title', {
          recordName: values.accountName || '',
        })}
      />
      <AccountFormWizard mode="edit" recordId={id} initialValues={values} />
    </>
  );
};

export default AccountEdit;
